import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  TrendingUp, 
  Users, 
  DollarSign, 
  FileText, 
  Settings,
  BarChart3,
  Shield,
  FileSpreadsheet,
  ClipboardList,
  Leaf,
  LogOut,
  ChevronDown,
  Lightbulb,
  Briefcase,
  Stethoscope
} from 'lucide-react';
import Logo from '../Logo.js';

const Sidebar = ({ userRole = 'respondent' }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState({});

  const isActive = (path) => location.pathname === path;

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const groups = [
    {
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['admin', 'manager', 'respondent'] },
        { icon: ClipboardList, label: 'Pesquisas', path: '/pesquisas', roles: ['admin', 'manager', 'respondent'] },
        { icon: Stethoscope, label: 'Diagnósticos', path: '/diagnosticos', roles: ['admin', 'manager', 'respondent'] },
      ]
    },
    {
      key: 'empresa',
      title: 'MINHA EMPRESA',
      roles: ['admin', 'manager', 'respondent'],
      items: [
        { icon: Building2, label: 'Minha Empresa', path: '/minha-empresa', roles: ['admin', 'manager', 'respondent'] },
        { icon: Lightbulb, label: 'Indicadores Customizados', path: '/indicadores-customizados', roles: ['admin', 'manager', 'respondent'] },
      ]
    },
    {
      key: 'benchmark',
      title: 'BENCHMARK',
      roles: ['admin', 'manager', 'respondent'],
      items: [
        { icon: Building2, label: 'Empresas', path: '/empresas', roles: ['admin', 'manager'] },
        { icon: BarChart3, label: 'Indicadores RH', path: '/indicadores-rh', roles: ['admin', 'manager', 'respondent'] },
        { icon: Users, label: 'Headcount', path: '/headcount', roles: ['admin', 'manager', 'respondent'] },
        { icon: Shield, label: 'ESG & Pessoas', path: '/esg-pessoas', roles: ['admin', 'manager', 'respondent'] },
        { icon: DollarSign, label: 'Remuneração', path: '/remuneracao', roles: ['admin', 'manager', 'respondent'] },
        { icon: FileSpreadsheet, label: 'Benefícios', path: '/beneficios', roles: ['admin', 'manager', 'respondent'] },
      ]
    },
    {
      key: 'gestao',
      title: 'GESTÃO',
      roles: ['admin', 'manager'],
      items: [
        { icon: TrendingUp, label: 'Acordos Coletivos', path: '/acordos-coletivos', roles: ['admin', 'manager'] },
        { icon: Briefcase, label: 'Cargos Padrão', path: '/cargos-padrao', roles: ['admin', 'manager'] },
        { icon: FileText, label: 'Relatórios', path: '/relatorios', roles: ['admin', 'manager'] },
        { icon: Settings, label: 'Configurações', path: '/configuracoes', roles: ['admin', 'manager', 'respondent'] },
      ]
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    window.location.href = '/login';
  };

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-primary text-white shadow-lg z-50" data-testid="sidebar">
      <div className="flex flex-col h-full">
        <div className="p-6 border-b border-slate-700" data-testid="sidebar-logo">
          <Link to="/dashboard">
            <Logo className="text-white" size="default" />
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3" data-testid="sidebar-nav">
          {groups.map((group, gi) => {
            if (group.roles && !group.roles.includes(userRole)) return null;
            const visibleItems = group.items.filter(it => it.roles.includes(userRole));
            if (visibleItems.length === 0) return null;

            const isCollapsed = group.key ? collapsed[group.key] : false;
            const hasActiveChild = visibleItems.some(it => isActive(it.path));

            return (
              <div key={gi} className="mb-1">
                {group.title && (
                  <button
                    onClick={() => toggle(group.key)}
                    className="w-full flex items-center justify-between pt-4 pb-1.5 px-4 group cursor-pointer"
                    data-testid={`sidebar-group-${group.key}`}
                  >
                    <span className={`text-[10px] font-semibold tracking-wider uppercase transition-colors ${hasActiveChild && isCollapsed ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-400'}`}>
                      {group.title}
                    </span>
                    <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                  </button>
                )}
                <ul
                  className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
                    isCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100'
                  }`}
                >
                  {visibleItems.map(item => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors duration-200 text-sm ${
                            active
                              ? 'bg-accent text-white font-medium'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
